import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material';
import FormData from 'form-data';
import { useDispatch, useSelector } from 'react-redux';
import OnlinePredictionOutlinedIcon from '@mui/icons-material/OnlinePredictionOutlined';
import PauseOutlinedIcon from '@mui/icons-material/PauseOutlined';
import TodayIcon from '@mui/icons-material/Today';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import DoNotDisturbAltOutlinedIcon from '@mui/icons-material/DoNotDisturbAltOutlined';
import { useNavigate } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NextWeekIcon from '@mui/icons-material/NextWeek';
import ArticleIcon from '@mui/icons-material/Article';
import HandshakeIcon from '@mui/icons-material/Handshake';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import { OpenActiveIcon } from '../../../assets/icons/openFolder';
import {
    clearFilters,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    resetController,
    setController,
    setFilters,
    setInitialFilters,
} from '../../../store/slices/tableController';
import StatusCard from '../../../components/common/StatusUI/StatusCard';
import { Plus as PlusIcon } from '../../../assets/icons/plus';
import { Upload as UploadIcon } from '../../../assets/icons/upload';
import { styles } from './styles';
import CustomTable from '../../../components/common/CustomTable';
import PositionBulkUploadDialog from './PositionBulkUploadDialog';
import PositionTableActions from './PositionTableActions';
import {
    useGetStatusByPositionCountMutation,
    useSearchPositionsMutation,
    useGetPositionsMutation,
} from '../../../api/positions';
import { useColumns } from './columns';
import {
    updatePositions,
    updateSelectedPositions,
    updateAllSelectedPositions,
    resetSelectedPositions,
} from '../../../store/slices/positions';
import { searchChangeCRPolicesAction } from '../../../store/slices/listCRPolicies';
import PermissionChecker from '../../../components/common/PermissionChecker';
import { useSnackbar } from 'notistack';

const icons = {
    Draft: ArticleIcon,
    'Open Pending Intake': OpenActiveIcon,
    'Intake Scheduled': TodayIcon,
    'Intake Completed': EventAvailableIcon,
    'Open Active Sourcing': OnlinePredictionOutlinedIcon,
    'On Hold - HM Delay': MoreTimeIcon,
    'Offer Negotiation': RequestQuoteIcon,
    'Offer Released': NextWeekIcon,
    'Offer Accepted': HandshakeIcon,
    Filled: CheckCircleOutlineIcon,
    'On Hold': PauseOutlinedIcon,
    Cancelled: DoNotDisturbAltOutlinedIcon,
};

const PositionsList = (props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const controller = useSelector((state) => state.tableController);
    const [openBulkUploadDialog, setOpenBulkUploadDialog] = useState(false);
    const { positions, selectedPositions } = useSelector((state) => state.positions);
    const columns = useColumns(positions);
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);
    const currentUser = useSelector((state) => state.session.user);

    // RTK Query hooks
    const [getStatusByPositionCount, { isLoading: isStatusLoading }] = useGetStatusByPositionCountMutation();
    const [searchPositions, { isLoading: isSearchLoading }] = useSearchPositionsMutation();
    const [getPositions, { isLoading: isPositionsLoading }] = useGetPositionsMutation();

    const fetchData = useCallback(
        async (searched, filters, sort, sortBy, page, pageSize) => {
            try {
                setIsLoading(true);
                const statusResponse = await getStatusByPositionCount().unwrap();
                if (!statusResponse) return;
                if (statusResponse?.status === 'ERROR') enqueueSnackbar(statusResponse?.message, { variant: 'error' });

                if (searched) {
                    const query = new URLSearchParams({});
                    if (filters.length) {
                        filters.forEach((filter) => query.append(filter.property, filter.value));
                    }
                    if (sortBy) {
                        query.append('sort', sortBy);
                        query.append('order', sort);
                    }
                    query.append('offset', page * pageSize);
                    query.append('max', pageSize);

                    const response = await searchPositions(query).unwrap();
                    if (!response) return;
                    if (response.status === 'ERROR') throw new Error(response?.message);

                    if (response) {
                        dispatch(
                            updatePositions({
                                ...response,
                                ...statusResponse,
                            }),
                        );
                    }
                } else {
                    const query = new URLSearchParams({});
                    if (sortBy) {
                        query.append('sort', sortBy);
                        query.append('order', sort);
                    }
                    query.append('offset', page * pageSize);
                    query.append('max', pageSize);

                    const response = await getPositions(query).unwrap();
                    if (!response) return;
                    if (response.status === 'ERROR') throw new Error(response?.message);

                    if (response) {
                        dispatch(
                            updatePositions({
                                ...response,
                                ...statusResponse,
                            }),
                        );
                    }
                }
            } catch (error) {
                if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
                else enqueueSnackbar(error?.message || 'Failed to fetch data', { variant: 'error' });
            } finally {
                setIsLoading(false);
            }
        },
        [dispatch, getStatusByPositionCount, searchPositions, getPositions, enqueueSnackbar],
    );

    useEffect(() => {
        dispatch(resetSelectedPositions());
    }, []);

    useEffect(() => {
        if (fetchData)
            fetchData(
                controller.searched,
                controller.filters,
                controller.sort,
                controller.sortBy,
                controller.page,
                controller.pageSize,
            );
    }, [
        controller.searched,
        controller.filters,
        controller.sort,
        controller.sortBy,
        controller.page,
        controller.pageSize,
        fetchData,
    ]);

    useEffect(() => {
        dispatch(
            setInitialFilters([
                {
                    property: {
                        name: 'c',
                        type: 'string',
                    },
                    operator: {
                        value: '',
                        typesAccepted: [],
                    },
                    value: '',
                },
            ]),
        );
    }, []);

    useEffect(() => {
        const data = new FormData();
        data.append('s', 'Active');
        data.append('all', true);
        dispatch(searchChangeCRPolicesAction(data));
    }, [dispatch]);

    const handleFiltersApply = (newFilters) => {
        const parsedFilters = newFilters.map((filter) => ({
            property: filter.property.name,
            value: filter.value,
            operator: filter.operator.value,
        }));

        dispatch(setFilters(parsedFilters));
    };

    const handleFiltersClear = () => {
        dispatch(clearFilters());
    };

    const handlePageChange = (newPage) => {
        dispatch(onPageChange(newPage - 1));
    };

    const handlePageSizeChange = (newPageSize) => {
        dispatch(onPageSizeChange(newPageSize));
    };

    const handleSortChange = (event, property) => {
        const isAsc = controller.sortBy === property && controller.sort === 'asc';
        dispatch(
            onSortChange({
                sort: isAsc ? 'desc' : 'asc',
                sortBy: property,
            }),
        );
    };

    const handleRowClick = (id) => {
        navigate(`/positions/${id}`);
    };

    const handleBulkUploadResult = (result) => {
        setOpenBulkUploadDialog(false);
        result &&
            fetchData(
                controller.searched,
                controller.filters,
                controller.sort,
                controller.sortBy,
                controller.page,
                controller.pageSize,
            );
    };

    const handleSelect = (event, rowId) => {
        dispatch(
            updateSelectedPositions({
                isChecked: event.target.checked,
                rowId,
            }),
        );
    };

    const handleSelectAll = (event) => {
        dispatch(
            updateAllSelectedPositions({
                isChecked: event.target.checked,
            }),
        );
    };

    const handleStatusFilter = (value) => {
        if (!value)
            return dispatch(
                resetController({
                    initialFilters: [
                        {
                            property: {
                                name: 'c',
                                type: 'string',
                            },
                            operator: {
                                value: '',
                                typesAccepted: [],
                            },
                            value: '',
                        },
                    ],
                }),
            );
        else
            dispatch(
                setController({
                    page: 0,
                    filters: [
                        {
                            operator: '',
                            property: 's',
                            value: value,
                        },
                    ],
                    initialFilters: [
                        {
                            property: {
                                name: 's',
                                type: 'string',
                            },
                            operator: {
                                value: '',
                                typesAccepted: [],
                            },
                            value: value,
                        },
                    ],
                    searched: true,
                }),
            );
    };

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Positions</Typography>
                <Box>
                    <PermissionChecker
                        requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_IMPORT_POSITIONS_DATA']}
                        noAuthRedirect={false}
                    >
                        <Button
                            color="primary"
                            onClick={() => setOpenBulkUploadDialog(true)}
                            size="large"
                            startIcon={<UploadIcon fontSize="small" />}
                            variant="outlined"
                        >
                            Bulk Upload
                        </Button>
                        <PositionBulkUploadDialog
                            openBulkUploadDialog={openBulkUploadDialog}
                            onResult={handleBulkUploadResult}
                        />
                    </PermissionChecker>
                    {!currentUser?.isClientHqEnabled && (
                        <PermissionChecker
                            requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_CREATE_POSITION']}
                            noAuthRedirect={false}
                        >
                            <Button
                                color="primary"
                                onClick={() => navigate('/positions/add')}
                                size="large"
                                startIcon={<PlusIcon fontSize="small" />}
                                variant="contained"
                                sx={{
                                    ml: '24px',
                                    ':hover': {
                                        backgroundColor: 'secondary.main',
                                    },
                                }}
                            >
                                Add Position
                            </Button>
                        </PermissionChecker>
                    )}
                </Box>
            </Box>
            <Box pb="32px">
                <Grid item container spacing={3} justifyContent="flex-start" alignItems="stretch" columns={18}>
                    {(positions?.statusList || []).map((item) => {
                        const filteredStatus = controller.filters?.find((item) => item.property === 's')?.value;
                        return (
                            <Grid item xs={12} sm={3} md={3}>
                                <StatusCard
                                    Icon={icons[item]}
                                    label={item}
                                    filteredStatus={filteredStatus}
                                    value={positions.states[item]}
                                    handleStatusFilter={handleStatusFilter}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                            Positions: {positions.count}
                        </Typography>
                        <PositionTableActions
                            filters={controller.filters}
                            initialFilters={controller.initialFilters}
                            onFiltersApply={handleFiltersApply}
                            onFiltersClear={handleFiltersClear}
                            selectedPositions={selectedPositions}
                            filterProperties={columns}
                            action={() =>
                                fetchData(
                                    controller.searched,
                                    controller.filters,
                                    controller.sort,
                                    controller.sortBy,
                                    controller.page,
                                    controller.pageSize,
                                )
                            }
                        />
                        <CustomTable
                            isLoading={isLoading}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            onSelect={handleSelect}
                            onSelectAll={handleSelectAll}
                            onRowClick={handleRowClick}
                            onSortChange={handleSortChange}
                            page={controller.page + 1}
                            columns={columns}
                            rows={positions.data}
                            rowsCount={positions.count}
                            selectedRows={selectedPositions}
                            sort={controller.sort}
                            sortBy={controller.sortBy}
                            showRowSelector={true}
                            showPagignation={true}
                            keyColumn="id"
                            action={() =>
                                fetchData(
                                    controller.searched,
                                    controller.filters,
                                    controller.sort,
                                    controller.sortBy,
                                    controller.page,
                                    controller.pageSize,
                                )
                            }
                        />
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default PositionsList;
